import {IAppTypeMap, Ic, IDesignSolution} from "../../../models/common";
import {ISpec} from "../../../models/ISpec";
import {select} from "@angular-redux/store";
import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, Pipe, PipeTransform} from "@angular/core";
import {getResourceImagePath, isResourceImageAvailable} from "../../../models/view";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AnalyticsActions} from "../../../Redux/Actions/analytics.actions";
import {ActionsService} from "../../../Redux/Actions/actions.service";
import {DomSanitizer} from '@angular/platform-browser';
import {Observable} from "rxjs";
import * as R from "ramda";
import {TranslateService} from "@ngx-translate/core";
import {support_to_url} from "../../../common/utils";
import {cardsUrls} from "../cardsUrls";

@Pipe({
  name: 'highlight'
})
export class HighlightSearch implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: any, args: any): any {
    if (!args) {
      return value;
    }
    // Match in a case insensitive maneer
    const re = new RegExp(args, 'gi');
    const match = value.match(re);

    // If there's no match, just return the original value.
    if (!match) {
      return value;
    }

    const replacedValue = value.replace(re, "<mark>" + match[0] + "</mark>");
    return this.sanitizer.bypassSecurityTrustHtml(replacedValue);
  }
}

@Component({
  selector: "edsng-design-solution-item",
  templateUrl: "./design-solution-item.component.html",
  styleUrls: ["./design-solution-item.component.css"]
})
export class IcGridItemComponent implements OnInit, AfterViewInit {
  @select(["appCatalog", "appClass"]) appClass$: Observable<string>;
  @select(["appProjectTypes", "appTypeMap"]) appTypeMap$;
  @select(["appSpec", "spec", "value"]) valueSpec$;
  @select(["appCatalog", "appClass"]) appName$;
  @select(['appToolBar', 'search']) searchField$;
  @select(['appContext', 'frameMode']) inFrame$: Observable<boolean>;
  itemSpec: ISpec<number>;
  typeMap: IAppTypeMap;
  appName: string;
  searchTerm: string;

  @Input() designSolution: Ic;
  @Input() errorSpecification: boolean = true;
  @Output() selectDesignSolution: EventEmitter<IDesignSolution> = new EventEmitter<IDesignSolution>();

  resolution = "md"; // init value waiting for solve isue on ObservableMedia for initial value (beta.9)

  constructor(public snack: MatSnackBar, private act: ActionsService, private translate: TranslateService) {
  }

  ngOnInit() {
    this.valueSpec$.subscribe(x => (this.itemSpec = x));
    this.appTypeMap$.subscribe(y => (this.typeMap = y));
    this.appName$.subscribe(w => (this.appName = w));
    this.searchField$.subscribe(x => this.searchTerm = x);
  }

  ngAfterViewInit() {
  }

  select(): boolean {
    if (this.appName === 'digital_power') {
      this.startDesign();
    } else {
      this.selectDesignSolution.emit(this.designSolution);
    }
    return false;
  }

  getCarsdUrl(): string {
    const uri = this.designSolution.visibleOnGrid === 'true' ? (cardsUrls[this.designSolution.topology] as string).trim().toLowerCase() : '';
    return (uri.startsWith("http") || uri.startsWith("/"))
      ? uri
      : (window.origin + uri);
  }

  startDesign() {
    const url = this.getCarsdUrl();
    this.act.sendAction(AnalyticsActions.sendAnalyticsEvent(
      'start_design',
      'redirect',
      {
        details: url,
        topology: this.designSolution.topology,
        cpn: this.designSolution.CPN,
        rpn: this.designSolution.RPN,
        tag: "grid"
      }
    ));
    window.open(url, "_blank");
  }

  isIcPackageAvailable(): boolean {
    return isResourceImageAvailable("xs", this.designSolution.packageImage);
  }

  getPackageImage(): string {
    return getResourceImagePath("xs", this.designSolution.packageImage);
  }

  isSchematicAvailable(): boolean {
    return isResourceImageAvailable(
      this.resolution,
      this.designSolution.schematic
    );
  }

  isAutomotiveGrade(): boolean {
    return R.includes({ feature: {label: 'Automotive Grade'} }, this.designSolution.features);
  }

  hasEdsimSupport(): boolean {
    return this.designSolution.designData && this.designSolution.designData.offlineSimulations && this.designSolution.designData.offlineSimulations.length > 0;
  }

  getSchematicImage(): string {
    return getResourceImagePath(this.resolution, this.designSolution.schematic);
  }

  dsUrl(): string {
    return support_to_url('datasheet', this.translate.currentLang) + this.designSolution.RPN + '.pdf';
  }

  dbUrl(): string {
    return support_to_url('databrief', this.translate.currentLang) + this.designSolution.referenceDesign + '.pdf';
  }

  openDS(event: any) {
    event.stopPropagation();
    const dsUrl = this.dsUrl();
    this.act.sendAction(AnalyticsActions.sendAnalyticsEvent(
      'open_datasheet',
      'redirect',
      {
        details: dsUrl,
        topology: this.designSolution.topology,
        rpn: this.designSolution.RPN,
        cpn: this.designSolution.CPN,
        tag: "grid"}
      ));
  }

  openDB(event: any) {
    event.stopPropagation();
    const dbUrl = this.dbUrl();
    this.act.sendAction(AnalyticsActions.sendAnalyticsEvent(
      'open_datasheet',
      'redirect',
      {
        details: dbUrl,
        topology: this.designSolution.topology,
        rpn: this.designSolution.referenceDesign,
        cpn: this.designSolution.referenceDesign,
        tag: "grid"}
    ));
  }

  pfUrl(): string {
    return support_to_url('product_folder', this.translate.currentLang) + this.designSolution.RPN;
  }

  pfUrlDigitalPower(): string {
    return support_to_url('product_folder', this.translate.currentLang) + this.designSolution.referenceDesign;
  }

  openPF(event: any, isDigitalPower: boolean) {
    event.stopPropagation();
    this.act.sendAction(AnalyticsActions.sendAnalyticsEvent(
      'open_product_folder',
      'redirect',
      {
        details: isDigitalPower ? this.pfUrlDigitalPower() : this.pfUrl(),
        topology: this.designSolution.topology,
        rpn: isDigitalPower ? this.designSolution.referenceDesign : this.designSolution.RPN,
        cpn: isDigitalPower ? this.designSolution.referenceDesign : this.designSolution.CPN,
        tag: "grid"}
      ));
  }
}
